<template>
  <ContestEntryFormLayout :load-state="loadState" :error-message="errorMessage" :current-step="2"
    :title="t('contestEntryForm.step2.title')" :contest="microCmsContest">
    <ContestEntryWorkSelectForm v-model="form" :category-options="categoryOptions" :can-proceed="canProceed"
      :novels="novels" :extra-field-items="extraFieldItems" @back="onClickBack"
      @next="onClickNext" />
  </ContestEntryFormLayout>
</template>

<script lang="ts">
export default {
  metaInfo: {
    meta: [{ name: "robots", content: "none" }],
  },
};
</script>

<script setup lang="ts">
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { useI18n } from "vue-i18n-bridge";
import { NolaPfContestClient } from "@/lib/clients";
import { Novel } from "@/lib/models";
import { NolaPfMicroCmsContest } from "@/lib/models/nolaPfContest";
import { toContestDepartmentOptions } from "@/lib/utils/contestEntryDepartments";
import {
  pushWithContestEntryForms,
  pushWithTermsAgreement,
  readApplicantFormFromHistoryState,
  readTermsAgreementFromHistoryState,
  resolveApplicantFormHistoryState,
  resolveWorkFormHistoryState,
} from "@/lib/utils/contestEntryFormHistory";
import { areExtraAnswersComplete, visibleExtraFieldItems } from "@/lib/utils/contestEntryExtraFields";
import { isContestEntryWorkFormComplete } from "@/lib/utils/contestEntryWorkForm";
import ContestEntryFormLayout from "@/components/templates/ContestEntryFormLayout.vue";
import ContestEntryWorkSelectForm from "@/components/organisms/event/ContestEntryWorkSelectForm.vue";
import type { ContestEntryWorkFormValue } from "@/components/organisms/event/contestEntryWorkFormTypes";

type LoadState = "loading" | "loaded" | "error";

const nolaPfContestClient = new NolaPfContestClient();

const instance = getCurrentInstance();
const { t } = useI18n();

const loadState = ref<LoadState>("loading");
const errorMessage = ref("");
const microCmsContest = ref<NolaPfMicroCmsContest | null>(null);

const emptyForm = (): ContestEntryWorkFormValue => ({
  categoryId: "",
  workSource: "",
  novelId: "",
  novelTitle: "",
  novelImage: "",
  novelDescription: "",
  workTitle: "",
  synopsis: "",
  extraAnswers: {},
});

const form = ref<ContestEntryWorkFormValue>(emptyForm());

const contestId = computed(() => instance?.proxy.$route.params.id as string);

const novels = computed(() => (instance?.proxy.$store.getters["novelModule/novels"] as Novel[]) ?? []);

const categoryOptions = computed(() => toContestDepartmentOptions(microCmsContest.value?.departments));

const extraFieldItems = computed(() => visibleExtraFieldItems(microCmsContest.value?.extraFieldItems));

const canProceed = computed(
  () =>
    areExtraAnswersComplete(extraFieldItems.value, form.value.extraAnswers) &&
    isContestEntryWorkFormComplete(form.value, {
      categoryRequired: categoryOptions.value.length > 0,
    })
);

const step1Location = computed(() => ({
  name: "eventContestEntryFormStep1",
  params: { id: contestId.value },
}));

function validateHistoryState() {
  const id = contestId.value;
  if (!id) return false;
  return resolveApplicantFormHistoryState(id) !== null;
}

async function loadPageData() {
  const id = contestId.value;
  if (!id || !validateHistoryState()) {
    errorMessage.value = t("contestEntryForm.step2.loadError");
    loadState.value = "error";
    return;
  }

  loadState.value = "loading";

  try {
    const microCmsContestResult = await nolaPfContestClient.fetchMicroCmsContest(id);
    microCmsContest.value = microCmsContestResult;

    await instance!.proxy.$store.dispatch("novelModule/initialize");

    const savedWorkForm = resolveWorkFormHistoryState(id);
    if (savedWorkForm) {
      form.value = savedWorkForm;
    }

    loadState.value = "loaded";
  } catch (error) {
    console.error("Failed to load contest entry form step2:", error);
    errorMessage.value = t("contestEntryForm.step2.contestLoadError");
    loadState.value = "error";
  }
}

async function onClickBack() {
  const id = contestId.value;
  if (id && readTermsAgreementFromHistoryState(id)) {
    await pushWithTermsAgreement((location) => instance!.proxy.$router.push(location), step1Location.value, id);
    return;
  }
  await instance!.proxy.$router.push(step1Location.value);
}

async function onClickNext() {
  if (!canProceed.value) return;

  const id = contestId.value;
  const workForm = { ...form.value };
  const applicantForm = readApplicantFormFromHistoryState(id);
  if (!applicantForm) return;

  await pushWithContestEntryForms(
    (location) => instance!.proxy.$router.push(location),
    {
      name: "eventContestEntryFormStep3",
      params: { id },
    },
    id,
    { applicantForm, workForm }
  );
}

onMounted(() => {
  loadPageData();
});
</script>
